/**
 * URL validation utilities.
 *
 * Single source of truth for "is this a URL we are willing to navigate to?".
 *
 * Any user-supplied URL that ends up in `window.open`, an `href`, or a
 * `window.location` assignment must be checked with `isValidHttpUrl` first.
 * A stored `javascript:` URI executes in the victim's session on our own
 * origin (EN-5095 / AppCheck 4.2), and `data:`, `vbscript:` and `file:` are
 * equally unsafe.
 *
 * We allow-list `http:`/`https:` rather than blacklisting `javascript:`:
 * a blacklist misses casing tricks (`JaVaScRiPt:`), embedded whitespace
 * (`java\nscript:`) and every scheme nobody thought of. The URL constructor
 * normalises all of those before we look at the protocol.
 *
 * Note that a scheme-relative URL (`//evil.tld`) is rejected too — `new URL()`
 * throws on it without a base, which is exactly the behaviour we want.
 */

/**
 * Maximum accepted URL length. Matches the server-side limit enforced by
 * `POST /v1/Shared/Appointments`, so the user gets an inline error instead
 * of a 400.
 */
export const MAX_URL_LENGTH = 2048;

/**
 * Returns true only for an absolute `http:`/`https:` URL of acceptable length.
 *
 * @example
 * isValidHttpUrl("https://teams.microsoft.com/l/meetup-join/x"); // true
 * isValidHttpUrl("javascript:alert(1)");                        // false
 * isValidHttpUrl("data:text/html,<script>alert(1)</script>");   // false
 * isValidHttpUrl("//evil.tld");                                 // false
 * isValidHttpUrl("");                                           // false
 */
export function isValidHttpUrl(value?: string | null): boolean {
  if (typeof value !== "string") {
    return false;
  }

  const trimmed = value.trim();

  if (trimmed === "" || trimmed.length > MAX_URL_LENGTH) {
    return false;
  }

  try {
    const { protocol } = new URL(trimmed);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Returns the trimmed URL when it is safe to navigate to, otherwise
 * `undefined`. Use this at call sites that need both "is it safe?" and the
 * value itself, so the unsafe value can never leak into navigation.
 */
export function getSafeHttpUrl(value?: string | null): string | undefined {
  return isValidHttpUrl(value) ? (value as string).trim() : undefined;
}

/**
 * Opens `value` in a new tab, but only if it is a valid http(s) URL.
 *
 * `noopener,noreferrer` prevents the opened page from reaching back through
 * `window.opener` (reverse tabnabbing).
 *
 * @returns true if the URL was opened, false if it was rejected.
 */
export function openExternalUrl(value?: string | null): boolean {
  const safeUrl = getSafeHttpUrl(value);

  if (!safeUrl) {
    return false;
  }

  window.open(safeUrl, "_blank", "noopener,noreferrer");
  return true;
}
